// Search suggestions under the site search box: matching categories and products from Shopify's
// predictive search, in a list the shopper can tap or walk with the arrow keys (ARIA combobox).
// With the box empty, the same list offers the shopper's recent searches and "Popular searches" (the
// search box hints). Built with textContent only, so names from the server and stored words never
// become HTML. Without this file the search box still works: it submits to the search page.
const DEBOUNCE_MS = 250;
const MIN_CHARS = 2;
const TIMEOUT_MS = 8000;
const LIMIT = 6;
// 40px thumbnails, sharp on screens up to 3x.
const THUMB_WIDTH = 120;
const RECENT_KEY = 'recent-searches';
const RECENT_MAX = 5;
const RECENT_MAX_LENGTH = 80;
const POPULAR_MAX = 8;

// Recently viewed products (sections/recently-viewed.liquid, assets/recently-viewed.js reads the
// list). This is the one script on every page that this feature may extend (the layout and the
// product section belong to other work), so product pages note their handle here: newest first, at
// most 12 (a few spare for products that have since gone).
const VIEWED_KEY = 'recently-viewed';
const VIEWED_MAX = 12;
const viewedHandle = window.location.pathname.match(/\/products\/([\w%-]+)\/?$/)?.[1];
function noteViewed() {
  try {
    const old = JSON.parse(localStorage.getItem(VIEWED_KEY) || '[]');
    const others = Array.isArray(old) ? old.filter((handle) => handle !== viewedHandle) : [];
    localStorage.setItem(VIEWED_KEY, JSON.stringify([viewedHandle, ...others].slice(0, VIEWED_MAX)));
  } catch {
    // Storage blocked or unreadable: the row stays hidden.
  }
}
// A prerendered product page (layout/theme.liquid) only counts as viewed once the shopper opens it.
if (viewedHandle) {
  if (document.prerendering) document.addEventListener('prerenderingchange', noteViewed, { once: true });
  else noteViewed();
}

const strings = document.getElementById('PredictiveSearchStrings')?.dataset;
const localeRoot = window.Shopify?.routes?.root || '/';
// Shopify's predictive search answers 417 for languages it does not support (Hindi and Telugu,
// checked 09-10-2026). Then the English index answers and result links get the language prefix back.
// The tab remembers the 417, so later pages skip the request that always fails.
const FALLBACK_KEY = `predictive-search-fallback:${localeRoot}`;
let endpointRoot = localeRoot;
try {
  if (sessionStorage.getItem(FALLBACK_KEY)) endpointRoot = '/';
} catch {
  // Storage blocked (private mode): the first request on each page finds the fallback again.
}

// The English index names categories in English. On Hindi and Telugu pages each category's name comes
// from its own page in the shopper's language (sections/collection-title.liquid): one small request per
// category, remembered on this page. Any failure (an error status too) keeps the English name and is
// asked again next time. English pages get null: nothing to ask. Brand categories (the header's brand
// list) keep their English name: brand names are never translated.
const localTitles = new Map();
const keepTitles = new Set((document.querySelector('[data-predictive-search]')?.dataset.keepTitles || '').split(','));
function localTitle(item, signal) {
  if (endpointRoot === localeRoot || typeof item.handle !== 'string' || keepTitles.has(item.handle)) return null;
  if (!localTitles.has(item.handle)) {
    const url = `${localeRoot}collections/${encodeURIComponent(item.handle)}?section_id=collection-title`;
    const title = fetch(url, { signal })
      .then((response) => (response.ok ? response.text() : Promise.reject(new Error(String(response.status)))))
      .then((html) => new DOMParser().parseFromString(html, 'text/html').body.textContent.trim() || item.title)
      .catch(() => {
        localTitles.delete(item.handle);
        return item.title;
      });
    localTitles.set(item.handle, title);
  }
  return localTitles.get(item.handle);
}

function element(tag, className, text = '') {
  const node = document.createElement(tag);
  node.className = className;
  node.textContent = text;
  return node;
}

/** A row of the list. Focus stays in the search box (aria-activedescendant), so rows are not Tab stops. */
function asOption(node) {
  node.tabIndex = -1;
  node.setAttribute('role', 'option');
  node.setAttribute('aria-selected', 'false');
  return node;
}

/** The shopper's last searches, newest first: [] when storage is blocked or holds something else. */
function readRecent() {
  try {
    const list = JSON.parse(localStorage.getItem(RECENT_KEY) || '[]');
    return Array.isArray(list) ? list.filter((term) => typeof term === 'string' && term.trim()).slice(0, RECENT_MAX) : [];
  } catch {
    return [];
  }
}

function writeRecent(list) {
  try {
    if (list.length) localStorage.setItem(RECENT_KEY, JSON.stringify(list));
    else localStorage.removeItem(RECENT_KEY);
  } catch {
    // Storage blocked (private mode): searches are not remembered.
  }
}

function rememberSearch(words) {
  const term = words.trim().slice(0, RECENT_MAX_LENGTH);
  if (!term) return;
  const same = term.toLowerCase();
  writeRecent([term, ...readRecent().filter((old) => old.toLowerCase() !== same)].slice(0, RECENT_MAX));
}

/**
 * "1657.50" → "Rs. 1,658": the suggest JSON price put into the shop's money format, as Liquid's
 * money filter prints it on the cards (no-decimals formats round to whole rupees). Text only.
 * Limit: handles {{amount}} and {{amount_no_decimals}} (this shop's format); other formats need more.
 */
function formatPrice(value) {
  const amount = Number(value);
  if (!value || !Number.isFinite(amount)) return '';
  return (strings.moneyFormat || '{{amount}}').replace(/\{\{\s*(\w+)\s*\}\}/, (_, name) => {
    const text = name.includes('no_decimals') ? String(Math.round(amount)) : amount.toFixed(2);
    const [whole, decimals] = text.split('.');
    const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return decimals ? `${grouped}.${decimals}` : grouped;
  });
}

/**
 * The price line of a product row. The suggest JSON has no stock per pack, so when packs cost
 * different amounts the row says "From" the lowest one (the card shows the first pack in stock).
 */
function productPrice(item) {
  const price = formatPrice(item.price_min);
  return price && item.price_max !== item.price_min ? strings.priceFrom.replace('__P__', price) : price;
}

/** A same-site link in the shopper's language, or null for anything else (e.g. "javascript:"). */
function resultHref(url) {
  if (typeof url !== 'string') return null;
  const href = new URL(url, window.location.origin);
  if (href.origin !== window.location.origin) return null;
  if (endpointRoot !== localeRoot) href.pathname = localeRoot + href.pathname.slice(1);
  return href.href;
}

function thumbnail(src) {
  const media = element('span', 'predictive-search__media');
  if (typeof src !== 'string') return media;
  const url = new URL(src, window.location.origin);
  url.searchParams.set('width', THUMB_WIDTH);
  const image = new Image(40, 40);
  image.alt = '';
  image.src = url.href;
  media.append(image);
  return media;
}

function createOption(url, image, title, detail) {
  const href = resultHref(url);
  if (!href) return null;
  const option = asOption(element('a', 'predictive-search__option'));
  option.href = href;
  const text = element('span', 'predictive-search__text');
  text.append(element('span', 'predictive-search__title', title), element('span', 'predictive-search__detail', detail));
  option.append(thumbnail(image), text);
  return option;
}

/** Turns one search form's [data-predictive-search] element into its suggestion list. */
function setup(listbox, index) {
  const form = listbox.closest('form');
  const input = form?.querySelector('input[name="q"]');
  if (!input) return;

  // Screen readers hear how many suggestions there are without leaving the box. A <div>, because
  // critical.css hides an empty <p>, and a live region that is hidden while empty is often not read.
  const status = element('div', 'visually-hidden');
  status.setAttribute('role', 'status');
  form.append(status);

  listbox.id = `PredictiveSearch-${index}`;
  listbox.hidden = true;
  listbox.setAttribute('role', 'listbox');
  listbox.setAttribute('aria-label', strings.label);
  input.setAttribute('role', 'combobox');
  input.setAttribute('aria-autocomplete', 'list');
  input.setAttribute('aria-controls', listbox.id);
  input.setAttribute('aria-expanded', 'false');

  let debounce;
  let controller;
  const options = () => [...listbox.querySelectorAll('[role="option"]')];

  function setActive(active) {
    for (const option of options()) option.setAttribute('aria-selected', String(option === active));
    if (active) {
      input.setAttribute('aria-activedescendant', active.id);
      active.scrollIntoView({ block: 'nearest' });
    } else {
      input.removeAttribute('aria-activedescendant');
    }
  }

  function toggle(open) {
    if (!open) setActive(null);
    listbox.hidden = !open;
    input.setAttribute('aria-expanded', String(open));
  }

  // Also stops the waiting or running request, so a late answer cannot open the list again.
  function close() {
    clearTimeout(debounce);
    controller?.abort();
    controller = null;
    toggle(false);
  }

  // Also empties the list and the status, so nothing old is read out or shown again.
  function clear() {
    close();
    status.textContent = '';
    listbox.replaceChildren();
  }

  function show(children, message) {
    // The new rows reuse the old ids, so the box must stop pointing at one.
    setActive(null);
    listbox.replaceChildren(...children);
    const found = options();
    found.forEach((option, i) => {
      option.id = `${listbox.id}-option-${i}`;
    });
    status.textContent = message || (found.length ? strings.count.replace('__N__', found.length) : strings.empty);
    toggle(found.length > 0);
  }

  function render(results = {}, query = '', signal) {
    const rows = [
      ...(results.collections || []).map((item) => {
        const option = createOption(item.url, item.featured_image?.url, item.title, strings.category);
        // Hindi and Telugu: the list shows at once with the English name, which changes to the local
        // one when it arrives (a row that a newer list replaced just changes off screen).
        if (option) {
          localTitle(item, signal)?.then((title) => {
            option.querySelector('.predictive-search__title').textContent = title;
          });
        }
        return option;
      }),
      ...(results.products || []).map((item) =>
        createOption(item.url, item.featured_image?.url || item.image, item.title, productPrice(item)),
      ),
    ].filter(Boolean);
    if (rows.length || !query) {
      show(rows);
      return;
    }
    // Nothing matched: one row that opens the full search, which also forgives spelling mistakes.
    const none = words.noResults.replace('__Q__', () => query);
    const option = termOption(query);
    option.className = 'predictive-search__option';
    const text = element('span', 'predictive-search__text');
    text.append(element('span', 'predictive-search__title', none), element('span', 'predictive-search__all', words.searchAll));
    option.replaceChildren(text);
    show([option], none);
  }

  // ---------- Empty box: recent and popular searches ----------

  const words = listbox.dataset;
  const popular = (input.dataset.searchHints || '')
    .split('\n')
    .map((hint) => hint.trim())
    .filter(Boolean)
    .slice(0, POPULAR_MAX);

  /** A search for these words, as the form would send it (the form's action keeps /hi or /te). */
  function termOption(term) {
    const url = new URL(form.action);
    url.searchParams.set('q', term);
    url.searchParams.set('options[prefix]', 'last');
    const option = asOption(element('a', 'chip predictive-search__term', term));
    option.href = url.href;
    option.dataset.term = term;
    return option;
  }

  // A listbox may hold groups of options; the group's name is a plain line inside it (ARIA pattern).
  function group(label, items, key) {
    const box = element('div', 'predictive-search__group');
    const title = element('div', 'predictive-search__label', label);
    title.id = `${listbox.id}-${key}`;
    title.setAttribute('role', 'presentation');
    box.setAttribute('role', 'group');
    box.setAttribute('aria-labelledby', title.id);
    box.append(title, ...items);
    return box;
  }

  function showIdle() {
    const recent = readRecent();
    const groups = [];
    if (recent.length) {
      // An option, not a button: inside a listbox only options can be picked with the arrow keys.
      const clearOption = asOption(element('span', 'chip predictive-search__term predictive-search__clear', words.clear));
      clearOption.setAttribute('aria-label', words.clearLabel);
      clearOption.dataset.clearRecent = '';
      groups.push(group(words.recent, [...recent.map(termOption), clearOption], 'recent'));
    }
    if (popular.length) groups.push(group(words.popular, popular.map(termOption), 'popular'));
    if (groups.length) show(groups);
  }

  function openIdle() {
    if (!input.value.trim() && listbox.hidden) showIdle();
  }

  input.addEventListener('focus', openIdle);
  // A tap on the box that already has focus (after Esc, or on a phone) opens the list again.
  input.addEventListener('click', openIdle);

  form.addEventListener('submit', () => rememberSearch(input.value));

  listbox.addEventListener('click', (event) => {
    if (event.target.closest('[data-clear-recent]')) {
      writeRecent([]);
      input.focus();
      clear();
      showIdle();
      return;
    }
    const term = event.target.closest('[data-term]')?.dataset.term;
    if (term) rememberSearch(term);
  });

  async function suggest(query) {
    controller?.abort();
    const request = new AbortController();
    controller = request;
    // One controller does both jobs: a newer keystroke aborts it, and so does this timer
    // (AbortSignal.timeout is missing on older phones; this works everywhere).
    setTimeout(() => request.abort(), TIMEOUT_MS);
    const params = new URLSearchParams({ q: query, 'resources[type]': 'product,collection', 'resources[limit]': LIMIT });
    try {
      // A plain GET without an Accept header, like a page request.
      const response = await fetch(`${endpointRoot}search/suggest.json?${params}`, { signal: request.signal });
      if (response.status === 417 && endpointRoot !== '/') {
        endpointRoot = '/';
        try {
          sessionStorage.setItem(FALLBACK_KEY, '1');
        } catch {
          // Storage blocked: only this page keeps the fallback.
        }
        return suggest(query);
      }
      if (!response.ok) throw new Error(`Search suggestions failed (${response.status})`);
      const data = await response.json();
      if (controller === request) render(data.resources?.results, query, request.signal);
    } catch (error) {
      if (controller !== request) return; // A newer keystroke took over.
      if (error.name !== 'AbortError') console.error(error);
      toggle(false);
    }
  }

  input.addEventListener('input', () => {
    clearTimeout(debounce);
    const query = input.value.trim();
    // Typing hides recent and popular searches; from 2 letters, suggestions take their place.
    if (query.length < MIN_CHARS || listbox.querySelector('[role="group"]')) clear();
    if (!query) showIdle();
    else if (query.length >= MIN_CHARS) debounce = setTimeout(suggest, DEBOUNCE_MS, query);
  });

  input.addEventListener('keydown', (event) => {
    const items = options();
    if (event.isComposing || !items.length) return;
    const activeIndex = items.findIndex((item) => item.getAttribute('aria-selected') === 'true');
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      const step = event.key === 'ArrowDown' ? 1 : -1;
      const first = step === 1 ? 0 : items.length - 1;
      toggle(true);
      setActive(items[activeIndex === -1 ? first : (activeIndex + step + items.length) % items.length]);
    } else if (event.key === 'Enter' && activeIndex !== -1) {
      event.preventDefault();
      items[activeIndex].click();
    } else if (event.key === 'Escape' && !listbox.hidden) {
      // Closes the list but keeps the words (the browser's own Escape would clear the box).
      event.preventDefault();
      close();
    }
  });

  // Tab or tap outside the form closes the list; a tap on a row inside it still follows the link.
  form.addEventListener('focusout', (event) => {
    if (event.relatedTarget && !form.contains(event.relatedTarget)) close();
  });
  document.addEventListener('pointerdown', (event) => {
    if (!form.contains(event.target)) close();
  });
}

if (strings) {
  // The list's styles load from here, not from <head>, so they never hold up the first paint.
  const stylesheet = Object.assign(document.createElement('link'), { rel: 'stylesheet', href: strings.stylesheet });
  document.head.append(stylesheet);
  document.querySelectorAll('[data-predictive-search]').forEach(setup);
}
